/**
 * Single source of truth for the construction upsert form route names.
 *
 * The create / edit / clone routes all render `ConstructionUpsert.vue` and are treated as one
 * "construction form" surface across navigation, filters and the layout collapse guards. Referencing
 * this constant instead of the raw string literals keeps every call site in sync if a route name
 * ever changes. Mirrors the existing `PROJECT_VIEW_ROUTE_NAMES` grouping in `router.ts`.
 *
 * This module intentionally has no imports so components can reference it without pulling in the
 * full `router.ts` dependency graph (which would create a circular import).
 */
export const ConstructionFormRoute = {
  Create: "libraryConstructionCreate",
  Edit: "libraryConstructionEdit",
  Clone: "libraryConstructionClone",
} as const;

export type ConstructionFormRouteName = (typeof ConstructionFormRoute)[keyof typeof ConstructionFormRoute];

/** All three construction-form route names — use for `.includes()` membership checks. */
export const CONSTRUCTION_FORM_ROUTE_NAMES: readonly ConstructionFormRouteName[] = [
  ConstructionFormRoute.Create,
  ConstructionFormRoute.Edit,
  ConstructionFormRoute.Clone,
];

/** True when the given route name is one of the construction upsert form routes. */
export const isConstructionFormRoute = (routeName: unknown): boolean =>
  typeof routeName === "string" && (CONSTRUCTION_FORM_ROUTE_NAMES as readonly string[]).includes(routeName);
